import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchBar from './components/SearchBar';
import FilterChips from './components/FilterChips';
import HeroCarousel from './components/HeroCarousel';
import EventGrid from './components/EventGrid';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocationEnabled, setIsLocationEnabled] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    category: 'all',
    date: null,
    price: null
  });
  const [eventCount, setEventCount] = useState(24);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated, signOut } = useAuth();

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleLocationToggle = () => {
    if (!isLocationEnabled) {
      // Request location permission
      if (navigator.geolocation) {
        navigator.geolocation?.getCurrentPosition(
          (position) => {
            setIsLocationEnabled(true);
          },
          (error) => {
            console.error('Location access denied:', error);
            setIsLocationEnabled(false);
          }
        );
      }
    } else {
      setIsLocationEnabled(false);
    }
  };

  const handleFilterChange = (filterId, filterType) => {
    if (filterType === 'reset') {
      setActiveFilters({
        category: 'all',
        date: null,
        price: null
      });
    } else {
      setActiveFilters(prev => ({
        ...prev,
        [filterType]: filterId
      }));
    }
  };

  const handleAuthAction = (action) => {
    navigate(`/${action}`);
  };

  const handleUserMenuAction = (action) => {
    setShowUserMenu(false);
    
    if (action === 'profile') {
      navigate('/my-account');
    } else if (action === 'tickets') {
      navigate('/my-tickets');
    } else if (action === 'admin') {
      if (userProfile?.role === 'admin') {
        navigate('/admin-portal-dashboard');
      } else if (userProfile?.role === 'manager') {
        navigate('/event-manager-dashboard');
      }
    } else if (action === 'logout') {
      signOut();
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event?.target?.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 bg-card border-b border-border shadow-elevation-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Ticket" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">Tixful</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button className="text-sm font-medium text-primary">
                Events
              </button>
              <button 
                onClick={() => navigate('/event-detail')}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                Browse
              </button>
            </nav>

            {/* Auth Actions */}
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/my-tickets')}
                    iconName="Ticket"
                  >
                    My Tickets
                  </Button>
                  
                  {/* User Profile Menu */}
                  <div className="relative user-menu-container">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-2"
                    >
                      {userProfile?.profile_image_url ? (
                        <img
                          src={userProfile?.profile_image_url}
                          alt={userProfile?.full_name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Icon name="User" size={14} color="white" />
                        </div>
                      )}
                      <Icon name="ChevronDown" size={16} />
                    </Button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-elevation-3 z-[1100]">
                        <div className="py-1">
                          <button
                            onClick={() => handleUserMenuAction('profile')}
                            className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                          >
                            <Icon name="User" size={16} className="mr-3" />
                            My Profile
                          </button>
                          
                          <button
                            onClick={() => handleUserMenuAction('tickets')}
                            className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                          >
                            <Icon name="Ticket" size={16} className="mr-3" />
                            My Tickets
                          </button>

                          {(userProfile?.role === 'admin' || userProfile?.role === 'manager') && (
                            <>
                              <div className="border-t border-border my-1"></div>
                              <button
                                onClick={() => handleUserMenuAction('admin')}
                                className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors"
                              >
                                <Icon name="Settings" size={16} className="mr-3" />
                                {userProfile?.role === 'admin' ? 'Admin Portal' : 'Manager Dashboard'}
                              </button>
                            </>
                          )}

                          <div className="border-t border-border my-1"></div>
                          <button
                            onClick={() => handleUserMenuAction('logout')}
                            className="flex items-center w-full px-4 py-2 text-sm text-destructive hover:bg-muted transition-colors"
                          >
                            <Icon name="LogOut" size={16} className="mr-3" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAuthAction('login')}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleAuthAction('register')}
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Section */}
        <div className="mb-8">
          <div className="text-center mb-6">
          </div>
          
          <SearchBar
            onSearch={handleSearch}
            onLocationToggle={handleLocationToggle}
            isLocationEnabled={isLocationEnabled}
          />
        </div>

        {/* Hero Carousel */}
        <HeroCarousel />

        {/* Filters */}
        <FilterChips
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          eventCount={eventCount}
        />

        {/* Events Grid */}
        <EventGrid
          filters={activeFilters}
          searchQuery={searchQuery}
          isLocationEnabled={isLocationEnabled}
        />
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Ticket" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">Tixful</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your premier destination for discovering and booking amazing events.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Explore</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button className="hover:text-foreground transition-colors">Browse Events</button></li>
                <li><button className="hover:text-foreground transition-colors">Categories</button></li>
                <li><button className="hover:text-foreground transition-colors">Venues</button></li>
                <li><button className="hover:text-foreground transition-colors">Popular Events</button></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button className="hover:text-foreground transition-colors">Help Center</button></li>
                <li><button className="hover:text-foreground transition-colors">Contact Us</button></li>
                <li><button className="hover:text-foreground transition-colors">Refund Policy</button></li>
                <li><button className="hover:text-foreground transition-colors">Terms of Service</button></li>
              </ul>
            </div>

            {/* Business */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">For Organizers</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button className="hover:text-foreground transition-colors">Create Events</button></li>
                <li><button className="hover:text-foreground transition-colors">Manage Tickets</button></li>
                <li><button className="hover:text-foreground transition-colors">Analytics</button></li>
                <li><button className="hover:text-foreground transition-colors">Pricing</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} Tixful. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 sm:mt-0">
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="Facebook" size={20} />
              </button>
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="Twitter" size={20} />
              </button>
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="Instagram" size={20} />
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;